import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ImportResult } from '../types';

const RESULT_ROWS: {
  key: keyof Pick<ImportResult, 'controllers' | 'assets' | 'alarmRules' | 'widgets'>;
  label: string;
}[] = [
  { key: 'controllers', label: 'Controllers' },
  { key: 'assets', label: 'Assets' },
  { key: 'alarmRules', label: 'Alarm Rules' },
  { key: 'widgets', label: 'Widgets' },
];

type ResultStepProps = {
  result: ImportResult;
};

export const ResultStep = ({ result }: ResultStepProps) => {
  const hasFailures = result.failures.length > 0;

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {hasFailures ? (
          <ErrorOutlineIcon color="warning" />
        ) : (
          <CheckCircleOutlineIcon color="success" />
        )}
        <Typography variant="h6">
          {hasFailures
            ? `Import completed with ${result.failures.length} error${
                result.failures.length !== 1 ? 's' : ''
              }`
            : 'Import completed'}
        </Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Resource</TableCell>
            <TableCell align="right">Created</TableCell>
            <TableCell align="right">Updated</TableCell>
            <TableCell align="right">Excluded</TableCell>
            <TableCell align="right">Failed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {RESULT_ROWS.map(({ key, label }) => (
            <TableRow key={key}>
              <TableCell>{label}</TableCell>
              <TableCell align="right">
                <Chip
                  size="small"
                  label={result[key].created}
                  color={result[key].created > 0 ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  size="small"
                  label={result[key].updated}
                  color={result[key].updated > 0 ? 'info' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  size="small"
                  label={result[key].excluded}
                  color="default"
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  size="small"
                  label={result[key].failed}
                  color={result[key].failed > 0 ? 'error' : 'default'}
                  variant="outlined"
                />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>Site Preview</TableCell>
            <TableCell align="right" colSpan={4}>
              {result.sitePreview.created > 0 ? (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Chip
                    size="small"
                    label={`Updated with ${result.sitePreview.created} tag${
                      result.sitePreview.created !== 1 ? 's' : ''
                    }`}
                    color="success"
                    variant="outlined"
                  />
                  {result.sitePreview.excluded > 0 && (
                    <Chip
                      size="small"
                      label={`${result.sitePreview.excluded} excluded`}
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Stack>
              ) : (
                <Chip size="small" label="Not updated" color="default" variant="outlined" />
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {hasFailures && (
        <Box
          sx={{
            maxHeight: 160,
            overflowY: 'auto',
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Errors
          </Typography>
          {result.failures.map((msg, idx) => (
            <Typography key={idx} variant="caption" color="error.main" display="block">
              {msg}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
};
